import { useEffect } from "react";
import type { Mailbox } from "@dfws/contracts";
import { queryClient } from "./queries";
import { onChatEvent, onChatReconnect } from "../features/chat/chatData";
import { onOutboxEvent } from "../features/compose/composeData";

let current: WebSocket | null = null;
/** Sends a small client event (typing, presence) if the socket is open; these are best effort by design. */
export function socketSend(event: Record<string, unknown>) {
  if (current?.readyState === WebSocket.OPEN) current.send(JSON.stringify(event));
}

// Many server events can arrive in a burst during sync; refetch each affected list once, shortly after the burst.
const pending = new Set<number>();
let flushTimer: ReturnType<typeof setTimeout> | undefined;
function invalidateFolder(mailboxId: number) {
  pending.add(mailboxId);
  clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    for (const id of pending) void queryClient.invalidateQueries({ queryKey: ["threads", id] });
    pending.clear();
  }, 400);
}

/** One WebSocket per tab. Server events invalidate the matching queries; reconnects with backoff. */
export function useRealtime(enabled: boolean, me: string) {
  useEffect(() => {
    if (!enabled) return;
    let ws: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;
      ws = new WebSocket(url);
      current = ws;
      ws.onopen = () => {
        if (attempt > 0) onChatReconnect();
        // Events sent before this socket existed (first sync after sign-in, or while reconnecting) are lost,
        // so catch up once the socket is open.
        void queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
        void queryClient.invalidateQueries({ queryKey: ["threads"] });
        attempt = 0;
        if (document.visibilityState === "hidden") socketSend({ type: "presence", away: true });
      };
      ws.onmessage = (e) => {
        let ev: Record<string, unknown> & { type?: string; mailboxId?: number; mailboxes?: { id: number; unread: number; total: number }[]; added?: number; changed?: number; removed?: number; snippets?: number };
        try {
          ev = JSON.parse(String(e.data));
        } catch {
          return;
        }
        if (ev.type === "mail.changed" && ev.mailboxId !== undefined && (ev.added || ev.changed || ev.removed || ev.snippets)) invalidateFolder(ev.mailboxId);
        if (ev.type === "mailbox.counts" && ev.mailboxes) {
          const counts = new Map(ev.mailboxes.map((m) => [m.id, m]));
          queryClient.setQueryData<{ items: Mailbox[]; syncing: boolean }>(["mailboxes"], (d) =>
            d ? { ...d, items: d.items.map((m) => ({ ...m, ...(counts.get(m.id) ?? {}) })) } : d,
          );
          if (!queryClient.getQueryData<{ items: Mailbox[] }>(["mailboxes"])?.items.length) void queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
        }
        if (ev.type === "mutation.failed") void queryClient.invalidateQueries();
        if (ev.type?.startsWith("chat.")) onChatEvent(ev, me);
        if (ev.type?.startsWith("outbox.")) onOutboxEvent(ev as { type?: string; error?: string });
      };
      ws.onclose = (e) => {
        if (stopped || e.code === 4401) return;
        attempt++;
        timer = setTimeout(connect, Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5)));
      };
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      ws?.close();
    };
  }, [enabled, me]);
}
